import { ROUTES, PageTypes, PageStatus } from "@/App";
import AddNewModal from "@/components/AddNewModal/AddNewModal";
import {
  useDeleteTemplateMutation,
  DeleteTemplatePayload,
  useGetPagesQuery,
  useSaveTemplateMutation,
  Template,
  useCloneTemplateMutation,
  CloneTemplatePayload,
} from "@/redux/reducer/pages.api";
import {
  ActionButton,
  ActionsMenu,
  Box,
  Button,
  ButtonColors,
  Grid,
  Icon,
  LightTable,
  Modal,
  PageTitle,
  Row,
  TalentechActionButtonVariant,
  TalentechIcons,
} from "@talentech/components";
import React, { useState } from "react";
import { useNavigate, useMatch, ErrorResponse } from "react-router-dom";
import CloneTemplateModal from "@/components/CloneTemplateModal/CloneTemplateModal";

import { toast } from "react-toastify";
import { getUserSupportStatus } from "@talentech/bff-login";
import { useSelector } from "react-redux";

interface IPagesListProps {}

const PagesList: React.FC<IPagesListProps> = () => {
  const type = !!useMatch(ROUTES.PAGES) ? PageTypes.PAGE : PageTypes.JOB;

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const [deleteTemplateMutation, { isLoading }] = useDeleteTemplateMutation();
  const [deleteTemplateModal, setDeleteTemplateModal] =
    useState<DeleteTemplatePayload | null>(null);

  const [cloneTemplateMutation, { isLoading: isCloneLoading }] =
    useCloneTemplateMutation();
  const [cloneTemplateModal, setCloneTemplateModal] = useState<
    (CloneTemplatePayload & { newName: string }) | null
  >(null);

  const { templates } = useGetPagesQuery(
    {},
    {
      selectFromResult: (res) => ({
        templates:
          type === PageTypes.PAGE
            ? res.data?.page_templates || []
            : res.data?.job_templates || [],
      }),
    }
  );

  const [saveTemplate] = useSaveTemplateMutation();
  const handleSubmit = ({ form: { name } }: any) => {
    const formData = new FormData();
    formData.append("files", new Blob(['<html><body></body></html>'], { type: "text/html" }), `index.html`);
    formData.append(
      `files`,
      new Blob([JSON.stringify({})], { type: "application/json" }),
      `index.json`
    );

    const promise = saveTemplate({ type, data: { name, formData } }).unwrap();
    toast
      .promise(promise, {
        pending: "Creating...",
        success: "Template created",
        error: "Error creating template",
      })
      .then(({ id }) => {
        setTimeout(() => {
          setIsOpen(false);
          navigate(ROUTES.EDITOR.replace(":type", type).replace(":id", id), {
            viewTransition: true,
          });
        }, 1000);
      });
  };

  const onDeleteActionClick = (params: Partial<DeleteTemplatePayload>) => {
    setDeleteTemplateModal({ ...params, type } as DeleteTemplatePayload);
  };

  const onDeleteActionConfirmationClick = () => {
    if (deleteTemplateModal) {
      toast
        .promise(deleteTemplateMutation(deleteTemplateModal).unwrap(), {
          pending: "Deleting...",
          success: "Template deleted",
          error: {
            render({ data }: { data: ErrorResponse }) {
              return <>{data.data.detail}</>;
            },
          },
        })
        .then(() => {
          setDeleteTemplateModal(null);
        });
    }
  };

  const onCloneActionClick = (params: Partial<CloneTemplatePayload>) => {
    const templateToClone = templates.find((t) => t.id === params.id);
    if (templateToClone) {
      const defaultNewName = `Copy of ${templateToClone.name}`;
      setCloneTemplateModal({
        ...params,
        type,
        newName: defaultNewName,
      } as CloneTemplatePayload & { newName: string });
    }
  };

  const onCloneActionConfirmationClick = () => {
    if (cloneTemplateModal) {
      const { newName, ...cloneParams } = cloneTemplateModal;
      toast
        .promise(
          cloneTemplateMutation({
            ...cloneParams,
            new_name: newName,
          }).unwrap(),
          {
            pending: "Cloning...",
            success: "Template cloned successfully",
            error: "Error cloning template",
          }
        )
        .then(() => {
          setCloneTemplateModal(null);
        });
    }
  };
  
  const isSupport = useSelector(getUserSupportStatus);

  const cols = tableColumns({
    navigate,
    onDeleteActionClick,
    onCloneActionClick,
    type,
    isSupport
  });


  return (
    <>
      <PageTitle
        title={type === PageTypes.PAGE ? "Job listing" : "Job ads"}
        additionalContent={
          isSupport && <ActionButton
            color={ButtonColors.PRIMARY}
            label="Add New"
            variant={TalentechActionButtonVariant.VERTICAL}
            icon={TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_PLUS}
            onClick={() => setIsOpen(true)}
          />
        }
      />
      <Grid container className="mt-4">
        <Row>
          <Grid  xs={12}>
            <Box paper rounded spacing={3}>
              <LightTable columns={cols} data={templates} />
            </Box>
          </Grid>
        </Row>
      </Grid>

      <AddNewModal
        handleSubmit={handleSubmit}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        type={type}
      />

      <Modal
        showModal={!!deleteTemplateModal}
        onClose={() => setDeleteTemplateModal(null)}
        title="Delete template"
        buttons={[
          <Button
            key="button-3"
            label="close"
            onClick={() => setDeleteTemplateModal(null)}
            outline
            color={ButtonColors.PRIMARY}
          />,
          <Button
            key="button-4"
            isLoading={isLoading}
            icon={
              <Icon
                icon={TalentechIcons.REGULAR_01_INTERFACE_ESSENTIALS_DELETE_BIN}
              />
            }
            color={ButtonColors.DANGER}
            label="delete"
            onClick={onDeleteActionConfirmationClick}
          />,
        ]}
      >
        <div>
          Are you sure you want to delete this template:{" "}
          <strong>
            {templates.find((t) => t.id === deleteTemplateModal?.id)?.name}
          </strong>
          ?
        </div>
      </Modal>

      <CloneTemplateModal
        showModal={!!cloneTemplateModal}
        onClose={() => setCloneTemplateModal(null)}
        onCloneConfirm={onCloneActionConfirmationClick}
        isLoading={isCloneLoading}
        template={templates.find((t) => t.id === cloneTemplateModal?.id)}
        cloneData={cloneTemplateModal}
        setCloneData={setCloneTemplateModal}
      />
    </>
  );
};

export default PagesList;

export const tableColumns = ({
  navigate,
  onDeleteActionClick,
  onCloneActionClick,
  type,
  isSupport,
}: {
  navigate: ReturnType<typeof useNavigate>;
  onDeleteActionClick: (params: Partial<DeleteTemplatePayload>) => void;
  onCloneActionClick: (params: Partial<CloneTemplatePayload>) => void;
  type: PageTypes;
  isSupport: boolean;
}) => [
  {
    label: "Published",
    accessor: "published",
    Component: (props: Template) => (
      <Icon
        icon={
          props.published
            ? TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_CHECKMARK
            : TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_STOP
        }
        className={`text-${props.published ? "success" : "danger"}`}
      />
    ),
  },

  {
    label: "Draft",
    accessor: "draft",
    Component: (props: Template) => (
      <Icon
        icon={
          props.draft
            ? TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_CHECKMARK
            : TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_STOP
        }
        className={`text-${props.draft ? "success" : "danger"}`}
      />
    ),
  },

  {
    label: "Page name",
    accessor: "name",
    Component: (props: Template) => {
      return <div>{props.name}</div>;
    },
  },
  {
    label: "Actions",
    accessor: "name",
    Component: (props: Template) => {
      return (
        // @ts-ignore
        <ActionsMenu
          actions={[
            {
              label: "Edit",
              onClick: () =>
                navigate(`/editor/${type}/${props.id}`, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-pencil"></i>,
            },
            { divider: true },
            ...insertConditionally(!!props.config_published_url, {
              label: "Open",
              onClick: () => window.open(props.html_published_url, "_blank"),
              icon: <i className="fa-sharp fa-light fa-globe"></i>,
            }),
            ...insertConditionally(!!props.html_draft_url, {
              label: "Open Preview",
              onClick: () => window.open(props.html_draft_url, "_blank"),
              icon: (
                <i className="fa-sharp fa-light fa-magnifying-glass-waveform"></i>
              ),
            }),
            ...insertConditionally(props.html_draft_revision_urls?.length > 0, {
              label: "Revisions",
              onClick: () => navigate(`/editor/${type}/${props.id}?revisions=true`),
              icon: <i className="fa-sharp fa-light fa-clock-rotate-left"></i>,
            }),

            {
              divider: true,
            },
            ...insertConditionally(isSupport && !!props.html_published_url, {
              label: "Clone",
              onClick: () =>
                onCloneActionClick({
                  id: props.id,
                  status: PageStatus.PUBLISHED,
                }),
              icon: <i className="fa-sharp fa-light fa-clone"></i>,
            }),
            ...insertConditionally(
              isSupport && !!props.html_draft_url && !props.html_published_url,
              {
                label: "Clone Draft",
                onClick: () =>
                  onCloneActionClick({
                    id: props.id,
                    status: PageStatus.DRAFT,
                  }),
                icon: <i className="fa-sharp fa-light fa-clone"></i>,
              }
            ),

            ...insertConditionally(isSupport, {
              divider: true,
            }),
            ...insertConditionally(!!props.html_published_url, {
              label: "Unpublish",
              onClick: () =>
                onDeleteActionClick({
                  id: props.id,
                  status: PageStatus.PUBLISHED,
                }),
              className: "text-danger",
              icon: <i className="fa-sharp fa-light fa-clock-rotate-left"></i>,
            }),
            ...insertConditionally(!!props.html_draft_url, {
              label: "Delete Draft",
              onClick: () =>
                onDeleteActionClick({
                  id: props.id,
                  status: PageStatus.DRAFT,
                }),
              className: "text-danger",
              icon: <i className="fa-sharp fa-light fa-trash"></i>,
            }),
          ]}
        />
      );
    },
  },
];

const insertConditionally = (condition: boolean, value: any) => {
  return condition ? [value] : [];
};
